var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { createContext, useCallback, useEffect, useMemo, useState, } from "react";
import { identityApi } from "./identityApi";
import { getStateFromStorage, putStateInLocalStorage, removeStateFromLocalStorage, } from "./identityStorage";
const FAILURE_RETRY_MILLISECONDS = 10000;
const defaultIdentity = { type: "unknown" };
const defaultState = {
    status: null,
    detail: defaultIdentity,
};
/**
 * @description
 *  Provides a hook to retrieve the identity for the current user.  This retrieves the user state from local storage cache
 *  if not present it will fetch identity from the /my-account/api/identity endpoint.  This will return the identity from
 *  a server-side cookie or retrieve it from BLAPI.  To force fetching identity from BLAPI using config { refresh: true }
 *  will ensure the hooke returns the true BLAPI logged in state.  This is used for myaccount/checkout where we need a user
 *  state to reflect the BLAPI authentication.
 *
 * @example normal usage
 *  const { state } = useHavenIdentity({ client: "search" });
 *
 * @example (forced refresh to match BLAPI login state)
 *  const { state } = useHavenIdentity({ client: "checkout", refresh: true });
 *
 * @example (override the session/identity/havenIdentity base urls for local development)
 *  const { state } = useHavenIdentity({
 *    client: "l&d",
 *    sessionUrl: 'http://localhost:3001',
 *    identityUrl: 'http://localhost:3002',
 *    havenIdentityUrl: 'http://localhost:3000',
 *  });
 *
 *  @example (use the optional havenIdentityEnabled to disable service-havem-identity logout)
 *  const { state } = useHavenIdentity({
 *    client: "l&d",
 *    havenIdentityEnabled: false,
 *  });
 *
 * @param config
 */
export const useHavenIdentity = (config) => {
    const [state, setState] = useState(defaultState);
    const [session, setSession] = useState(null);
    const api = useMemo(() => identityApi({
        client: config.client,
        identityUrl: config === null || config === void 0 ? void 0 : config.identityUrl,
        sessionUrl: config === null || config === void 0 ? void 0 : config.sessionUrl,
        havenIdentityUrl: config === null || config === void 0 ? void 0 : config.havenIdentityUrl,
        havenIdentityEnabled: config === null || config === void 0 ? void 0 : config.havenIdentityEnabled,
    }), [
        config === null || config === void 0 ? void 0 : config.sessionUrl,
        config === null || config === void 0 ? void 0 : config.identityUrl,
        config === null || config === void 0 ? void 0 : config.client,
        config === null || config === void 0 ? void 0 : config.havenIdentityUrl,
        config === null || config === void 0 ? void 0 : config.havenIdentityEnabled,
    ]);
    const setFailedState = (e) => {
        updateState({
            status: "failed",
            error: e,
            lastFetch: Date.now(),
        });
    };
    const updateStateFromStorage = () => {
        const updatedState = getStateFromStorage();
        setState(updatedState || defaultState);
        return updatedState;
    };
    const updateState = (state) => {
        putStateInLocalStorage(state);
        setState(state);
    };
    const isUpdateAllowed = () => {
        if (state && state.status === "failed" && state.lastFetch) {
            const diff = Date.now() - state.lastFetch;
            return diff > FAILURE_RETRY_MILLISECONDS;
        }
        return true;
    };
    const updateIdentity = useCallback((...args_1) => __awaiter(void 0, [...args_1], void 0, function* (updateConfig = { refresh: true }) {
        try {
            if (!isUpdateAllowed())
                return;
            const current = updateStateFromStorage() || state; // use last state from storage or current
            setState(Object.assign(Object.assign({}, current), { status: "pending" }));
            const { session, data } = yield api.fetchIdentity(updateConfig);
            updateState({
                status: "success",
                detail: data || defaultIdentity,
                lastFetch: Date.now(),
            });
            setSession(session);
        }
        catch (e) {
            removeStateFromLocalStorage();
            setFailedState(e);
        }
    }), 
    // eslint-disable-next-line  react-hooks/exhaustive-deps
    [setState, api, state]);
    const clearIdentity = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        try {
            const logout = yield api.logout();
            if (logout) {
                updateState({
                    status: "success",
                    detail: defaultIdentity,
                    lastFetch: Date.now(),
                });
                setSession(null);
            }
            return logout;
        }
        catch (e) {
            setFailedState(e);
            removeStateFromLocalStorage();
            return false;
        }
        // eslint-disable-next-line  react-hooks/exhaustive-deps
    }), [api]);
    useEffect(() => {
        if (config === null || config === void 0 ? void 0 : config.refresh) {
            void updateIdentity();
        }
        else if (!state.status || state.status === "failed") {
            const updateFromStorage = updateStateFromStorage();
            if (!updateFromStorage)
                void updateIdentity({ refresh: false });
        }
        // eslint-disable-next-line  react-hooks/exhaustive-deps
    }, []);
    return { identity: state, updateIdentity, clearIdentity, session };
};
/**
 * @description
 * Allows identity to be provided using useContext.
 *
 * @example creating context
 *
 *   const identity = useHavenIdentity({
 *      identityUrl: gatewayApiUrl,
 *      sessionUrl: sessionApiUrl,
 *      havenIdentityUrl: havenIdentityApiUrl,
 *    }); // baseurl is optional - it will default to window.location.origin
 *        // havenIdentityUrl is optional - used for service-haven-identity logout
 *        // havenIdentityEnabled is optional - used to disable service-haven-identity logout
 *
 *   return (
 *   <IdentityContext.Provider value={identity}>
 *      <HeaderComponent />
 *      <main>
 *        <Component {...pageProps} />
 *      </main>
 *      <Footer analytics={tagManager} />
 *   </IdentityContext.Provider>
 *   );
 *
 * @example using context
 *   const identity = useContext(IdentityContext);
 */
export const IdentityContext = createContext({
    identity: defaultState,
    session: null,
    updateIdentity: () => Promise.resolve(),
    clearIdentity: () => Promise.resolve(false),
});
